import { Routes, UrlSegment } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';
import { CatalogueService } from './catalogue/catalogue.service';
import { InfoBlockService } from './catalogue/info-block/info-block.service';
import { ProductsService } from './catalogue/products/products.service';
import { ClaimsUnavailableComponent } from './claims-unavailable/claims-unavailable.component';
import { CompanyAccountComponent } from './company-account/company-account.component';
import { ConfiguratorModalService } from './configurator-modal/configurator-modal.service';
import { AppLanguagesInUrl } from './core/constants/routing.constants';
import { ContainerType } from './core/enums/container-type.enum';
import { GeneralRoutePaths, InquiriesRoutePath, OrderRoutePath, RoutePathParams, RouteTypeEnums } from './core/enums/route-types.enum';
import { UserRole } from './core/enums/user-role.enum';
import { AdminGuard } from './core/guards/admin.guard';
import { AuthGuard } from './core/guards/auth.guard';
import { changeExpiringPasswordGuard } from './core/guards/change-expiring-password/change-expiring-password.guard';
import { cataloguePageGuard, companyProfilePageGuard } from './core/guards/dealer-permissions/general-permissions-page.guard';
import { LanguageGuard } from './core/guards/language.guard';
import { leaveAndCoverageAccessGuard } from './core/guards/leave-and-coverage-access/leave-and-coverage-access.guard';
import { mobileDeviceGuard } from './core/guards/mobile-device.guard';
import { NotAdminGuard } from './core/guards/not-admin.guard';
import { onLeaveGuard } from './core/guards/on-leave.guard';
import { RegularUserGuard } from './core/guards/regular-user.guard';
import { RoleGuard } from './core/guards/role.guard';
import { RouteByRolesGuard } from './core/guards/route-by-roles.guard';
import { userAdminGuard } from './core/guards/user-admin/user-admin.guard';
import { UserGuard } from './core/guards/user.guard';
import { HomeComponent } from './core/home/home.component';
import { ImpersonateComponent } from './core/impersonate/impersonate.component';
import { ImpersonateResolver } from './core/impersonate/resolver/impersonate.resolver';
import { LogoutComponent } from './core/logout/logout.component';
import { OrderArticleService } from './core/services/order-article/order-article.service';
import { PageNotAllowedComponent } from './errors/page-not-allowed/page-not-alowed.component';
import { PageNotFoundComponent } from './errors/page-not-found/page-not-found.component';
import { SomethingWentWrongComponent } from './errors/something-went-wrong/something-went-wrong.component';
import { UnsupportedBrowserComponent } from './errors/unsupported-browser/unsupported-browser.component';
import { OnLeaveComponent } from './leave-and-coverage/on-leave/on-leave.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { ManagedUsersParentComponent } from './managed-users/managed-users-parent.component';
import { MessagesService } from './messages-modal/messages.service';
import { DiscountModalOpenerService } from './orders/discount-modal/discount-modal-opener.service';
import { OrderArticlesListService } from './orders/order-articles-list/services/order-articles-list/order-articles-list.service';
import { SelectedRowsService } from './orders/order-articles-list/services/selected-rows/selected-rows.service';
import { OrdersService } from './orders/orders.service';
import { OrderSearchProviderComponent } from './orders/search/order-search-provider/order-search-provider.component';
import { DocumentTemplateService } from './orders/services/document-template/document-template.service';
import { ExternalRedirectComponent } from './shared/components/external-redirect/external-redirect.component';
import { PricelistDownloadComponent } from './shared/components/pricelist-download/pricelist-download.component';
import { CommaPipe } from './shared/pipes/comma/comma.pipe';
import { I18nDatePipe } from './shared/pipes/i18n-date/i18n-date.pipe';
import { PriceFormatPipe } from './shared/pipes/price-format/price-format.pipe';
import { LoaderService } from './ui-elements/loader/loader.service';
import { UncachePipe } from './uncacheable-resource/uncache.pipe';

export const appRoutes: Routes = [
  { path: '', component: HomeComponent, canActivate: [RegularUserGuard, NotAdminGuard, AuthGuard] },
  {
    path: GeneralRoutePaths.SIGN_IN,
    loadChildren: () => import('./auth/auth.routing').then((m) => m.authRoutes),
    canActivate: [UserGuard],
    data: {
      containerType: ContainerType.FULL_WIDTH,
    },
  },
  {
    path: GeneralRoutePaths.LOGOUT,
    component: LogoutComponent,
    canActivate: [AuthGuard],
  },
  {
    path: GeneralRoutePaths.TERMS,
    loadChildren: () => import('./terms/terms-and-conditions.routing').then((m) => m.termsAndConditionsRoutes),
  },
  {
    path: `${GeneralRoutePaths.IMPERSONATE}/${RoutePathParams.EMAIL}`,
    canActivate: [AdminGuard],
    resolve: { email: ImpersonateResolver },
    component: ImpersonateComponent,
  },
  {
    path: GeneralRoutePaths.CATALOGUE,
    data: { id: RouteTypeEnums.CATALOGUE },
    canActivate: [RegularUserGuard, AuthGuard, cataloguePageGuard, onLeaveGuard],
    loadChildren: () => import('./catalogue/catalogue.routing').then((m) => m.catalogueRoutes),
    providers: [CatalogueService, ProductsService, InfoBlockService, NgbActiveModal, UncachePipe],
  },
  {
    path: GeneralRoutePaths.CONFIGURATION,
    canActivate: [RegularUserGuard, NotAdminGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./configurator-modal/configurator.routing').then((m) => m.configuratorRoutes),
    providers: [ConfiguratorModalService, OrderArticleService, LoaderService],
  },
  {
    path: OrderRoutePath.ROOT,
    data: {
      id: RouteTypeEnums.ORDERS,
      allowedRoles: [UserRole.ROLE_PM, UserRole.ROLE_DEALER, UserRole.ROLE_NARBUTAS_LT],
    },
    canActivate: [RegularUserGuard, NotAdminGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./orders/orders.routing').then((m) => m.ordersRoutes),
    component: OrderSearchProviderComponent,
    providers: [
      OrdersService,
      NgbActiveModal,
      RouteByRolesGuard,
      MessagesService,
      SelectedRowsService,
      OrderArticlesListService,
      SelectedRowsService,
      DiscountModalOpenerService,
      I18nDatePipe,
      DocumentTemplateService,
      PriceFormatPipe,
      CommaPipe,
    ],
  },
  {
    path: GeneralRoutePaths.USER,
    data: { id: RouteTypeEnums.USER },
    canActivate: [RegularUserGuard, NotAdminGuard, AuthGuard, onLeaveGuard],
    loadChildren: () => import('./user/user.routing').then((m) => m.userRoutes),
  },
  {
    path: GeneralRoutePaths.NEWS,
    data: {
      id: RouteTypeEnums.NEWS,
      containerType: ContainerType.FULL_WIDTH,
    },
    canActivate: [RegularUserGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./news/news.routing').then((m) => m.newsRoutes),
  },
  {
    path: GeneralRoutePaths.INFO,
    data: {
      id: RouteTypeEnums.INFO,
      uri: GeneralRoutePaths.INFO,
      containerType: ContainerType.FULL_WIDTH,
    },
    canActivate: [RegularUserGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./content/content.routing').then((m) => m.contentRoutes),
  },
  {
    path: GeneralRoutePaths.ESTIMATES,
    data: {
      id: RouteTypeEnums.ESTIMATES,
      containerType: ContainerType.FULL_WIDTH,
    },
    canActivate: [RegularUserGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./estimates/estimates.routing').then((m) => m.estimatesRoutes),
  },
  {
    path: GeneralRoutePaths.FAQ,
    data: {
      id: RouteTypeEnums.FAQ,
      containerType: ContainerType.FULL_WIDTH,
    },
    canActivate: [RegularUserGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./faq/faq.routing').then((m) => m.faqRoutes),
  },
  {
    path: GeneralRoutePaths.DOWNLOAD_PRICELIST,
    canActivate: [AuthGuard, onLeaveGuard],
    component: PricelistDownloadComponent,
  },
  {
    path: GeneralRoutePaths.FEATURE_FLAGS,
    data: {
      allowedRoles: `${
        environment.production
          ? [UserRole.ROLE_ADMIN]
          : [UserRole.ROLE_ADMIN, UserRole.ROLE_PM, UserRole.ROLE_DEALER, UserRole.ROLE_NARBUTAS_LT]
      }`,
      preload: false,
    },
    canActivate: [RegularUserGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./feature-flags/feature-flags.routing').then((m) => m.featureFlagsRoutes),
  },
  {
    path: GeneralRoutePaths.UI_INVENTORY,
    data: {
      allowedRoles: `${
        environment.production
          ? [UserRole.ROLE_ADMIN]
          : [UserRole.ROLE_ADMIN, UserRole.ROLE_PM, UserRole.ROLE_DEALER, UserRole.ROLE_NARBUTAS_LT]
      }`,
      preload: false,
    },
    canActivate: [RegularUserGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./component-inventory/component-inventory.routing').then((m) => m.componentInventoryRoutes),
  },
  {
    path: InquiriesRoutePath.ROOT,
    data: {
      id: RouteTypeEnums.INQUIRIES,
      uri: 'inquiries',
    },
    canActivate: [RegularUserGuard, AuthGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./inquiries/inquiries.routing').then((m) => m.inquiriesRoutes),
  },
  {
    path: GeneralRoutePaths.LEAVE_AND_COVERAGE,
    data: {
      allowedRoles: [UserRole.ROLE_PM],
    },
    canActivate: [RegularUserGuard, AuthGuard, leaveAndCoverageAccessGuard, RoleGuard, onLeaveGuard],
    loadChildren: () => import('./leave-and-coverage/leave-and-coverage.routing').then((m) => m.leaveAndCoverageRoutes),
  },
  {
    path: GeneralRoutePaths.MANAGED_USERS,
    canActivate: [RegularUserGuard, AuthGuard, userAdminGuard, onLeaveGuard],
    component: ManagedUsersParentComponent,
  },
  {
    path: GeneralRoutePaths.COMPANY_ACCOUNT,
    canActivate: [RegularUserGuard, AuthGuard, companyProfilePageGuard, onLeaveGuard],
    component: CompanyAccountComponent,
  },
  { path: GeneralRoutePaths.PAGE_NOT_FOUND, component: PageNotFoundComponent },
  { path: GeneralRoutePaths.SOMETHING_WENT_WRONG, component: SomethingWentWrongComponent },
  { path: GeneralRoutePaths.UNSUPPORTED_BROWSER, component: UnsupportedBrowserComponent },
  { path: GeneralRoutePaths.MAINTENANCE, component: MaintenanceComponent },
  {
    matcher: (segments: UrlSegment[]) => {
      if (segments.length === 0) return null;

      const lang = segments[0].path;
      if (!AppLanguagesInUrl.includes(lang)) return null;

      return {
        consumed: [segments[0]], // only consume the lang segment
        posParams: { language: segments[0] },
      };
    },
    canActivate: [LanguageGuard],
    children: [
      {
        path: GeneralRoutePaths.SIGN_IN,
        loadChildren: () => import('./auth/auth.routing').then((m) => m.authRoutes),
        canActivate: [UserGuard],
        data: {
          containerType: ContainerType.FULL_WIDTH,
        },
      },
      {
        path: GeneralRoutePaths.TERMS,
        loadChildren: () => import('./terms/terms-and-conditions.routing').then((m) => m.termsAndConditionsRoutes),
      },
      { path: GeneralRoutePaths.SOMETHING_WENT_WRONG, component: SomethingWentWrongComponent },
      { path: GeneralRoutePaths.UNSUPPORTED_BROWSER, component: UnsupportedBrowserComponent },
      {
        path: GeneralRoutePaths.CHANGE_EXPIRING_PASSWORD,
        canActivate: [changeExpiringPasswordGuard],
        // component will never be activated cause guard will always redirect
        component: HomeComponent,
      },
    ],
  },
  {
    path: GeneralRoutePaths.ON_LEAVE,
    component: OnLeaveComponent,
    canActivate: [AuthGuard],
  },
  {
    path: GeneralRoutePaths.CHANGE_EXPIRING_PASSWORD,
    canActivate: [changeExpiringPasswordGuard, onLeaveGuard],
    // component will never be activated cause guard will always redirect
    component: HomeComponent,
  },
  {
    path: GeneralRoutePaths.CLAIMS_UNAVAILABLE,
    component: ClaimsUnavailableComponent,
    canActivate: [mobileDeviceGuard, onLeaveGuard],
  },
  {
    path: GeneralRoutePaths.FORBIDDEN,
    component: PageNotAllowedComponent,
    canActivate: [AuthGuard],
  },
  {
    path: GeneralRoutePaths.SONATA,
    component: ExternalRedirectComponent,
    data: { target: environment.backOfficeURL },
    canActivate: [AuthGuard, AdminGuard, onLeaveGuard],
  },
  { path: '**', component: PageNotFoundComponent },
];
